import { AccessTime, CheckCircle, LocationOn, Person, PlayArrow, Schedule, Stop, Timer, WorkOutline } from "@mui/icons-material";
import { Alert, Avatar, Box, Divider, Grid, Paper, Skeleton, Step, StepLabel, Stepper, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { JobChatView } from "../../../../../../job-chats";
import { colors } from "../../../../../../ui/colors";
import { PunchButton } from "./components/PunchButton";
import { useJobPunch } from "./hooks/usePunchMutation";

export default function InProgressEmployee({ data, focusJobChat = false }) {
  const event = data?.events?.[0];
  const workforce = event?.workforce || [];

  const jobEventUid = data.events[0].uid; //EVENTO UNICO

  const { uid } = useSelector((state) => state.userState);

  const jobChatCompanyId = data?.companyId ?? workforce?.[0]?.companyId;

  const currentStaff = workforce.find((s) => s.userUid === uid || s.uid === uid);
  const jobEventData = currentStaff?.WorkforcesByJobEvents;

  const startAt = jobEventData?.startAt ? dayjs(jobEventData.startAt) : null;
  const endAt = jobEventData?.endAt ? dayjs(jobEventData.endAt) : null;

  const initialized = !startAt && !endAt;
  const started = startAt && !endAt;
  const finished = startAt && endAt;
  const eventStart = event?.startDate ? dayjs(event.startDate) : null;
  const isLate = initialized && eventStart && dayjs().isAfter(eventStart);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [currentStep, setCurrentStep] = useState(finished ? 2 : started ? 1 : 0);
  const jobChatRef = useRef(null);

  useEffect(() => {
    if (!focusJobChat) return;
    const timeout = window.setTimeout(() => {
      const chat = jobChatRef.current;
      if (!chat) return;

      chat.focus({ preventScroll: true });
      let scrollContainer = chat.parentElement;
      while (scrollContainer) {
        const { overflowY } = window.getComputedStyle(scrollContainer);
        if (["auto", "scroll"].includes(overflowY) && scrollContainer.scrollHeight > scrollContainer.clientHeight) break;
        scrollContainer = scrollContainer.parentElement;
      }

      if (scrollContainer) {
        const top = scrollContainer.scrollTop + chat.getBoundingClientRect().top - scrollContainer.getBoundingClientRect().top - 16;
        scrollContainer.scrollTo({ top, behavior: "smooth" });
      } else {
        chat.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 350);
    return () => window.clearTimeout(timeout);
  }, [focusJobChat, event?.uid]);

  const [startTime, setStartTime] = useState(startAt);
  const [endTime, setEndTime] = useState(endAt);
  const [elapsed, setElapsed] = useState(0);
  const [loadingTime, setLoadingTime] = useState(true);

  useEffect(() => {
    setCurrentStep(finished ? 2 : started ? 1 : 0);
  }, [initialized, started, finished]);

  useEffect(() => {
    if (!started || !startTime) return;

    const interval = setInterval(() => {
      setElapsed(dayjs().diff(startTime, "second"));
      setLoadingTime(false);
    }, 1000);

    return () => clearInterval(interval);
  }, [started, startTime]);

  const { startJob, isStarting, endJob, isEnding } = useJobPunch(jobEventUid);

  const formatElapsed = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return h > 0 ? `${h}h ${m}m ${sec}s` : `${m}m ${sec}s`;
  };

  const steps = [
    {
      label: isLate ? "Worker late" : "Waiting",
      description: isLate ? "Not punched in" : "Waiting for punch",
      icon: isLate ? <AccessTime /> : <Schedule />,
      color: isLate ? colors.orange.base : "#9e9e9e",
    },
    {
      label: "Active",
      description: "Work in progress",
      icon: <PlayArrow />,
      color: "#2196f3",
    },
    {
      label: "Done",
      description: "Completed",
      icon: <CheckCircle />,
      color: "#4caf50",
    },
  ];

  const client = event?.client || {};
  const location = event?.coverageArea || {};
  const clientName = `${client.firstName || ""} ${client.lastName || ""}`.trim();

  const getButton = () => {
    if (finished) return null;

    if (initialized) {
      return (
        <PunchButton
          isLoading={isStarting}
          onClick={async () => {
            const newStart = await startJob();
            setStartTime(newStart);
            setCurrentStep(1);
          }}
          icon={<PlayArrow />}
          label="Start Job"
          loadingLabel="Starting..."
          color="#2196f3"
          hoverColor="#1976d2"
        />
      );
    }

    if (started) {
      return (
        <PunchButton
          isLoading={isEnding}
          onClick={async () => {
            const newEnd = await endJob();
            setEndTime(newEnd);
            setCurrentStep(2);
          }}
          icon={<Stop />}
          label="Finish Job"
          loadingLabel="Finishing..."
          color="#f44336"
          hoverColor="#d32f2f"
        />
      );
    }

    return null;
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Grid container spacing={4} sx={{ maxWidth: 1200, mx: "auto" }}>
        {/* STEPPER */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography fontWeight={600} sx={{ mb: 2 }}>
              Status
            </Typography>

            <Stepper
              activeStep={currentStep}
              orientation={isMobile ? "horizontal" : "vertical"}
              sx={{
                flexWrap: isMobile ? "wrap" : "nowrap",
                "& .MuiStepConnector-line": {
                  minHeight: isMobile ? 0 : 32,
                },
              }}
            >
              {steps.map((s, i) => (
                <Step key={i} completed={i < currentStep}>
                  <StepLabel
                    StepIconComponent={(props) => {
                      const active = props.active || props.completed;
                      return (
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            backgroundColor: active ? s.color : isMobile ? "#e0e0e0" : "#f4a4a4",
                            color: "white",
                          }}
                        >
                          {s.icon}
                        </Box>
                      );
                    }}
                  >
                    {!isMobile && (
                      <Box sx={{ ml: 1 }}>
                        <Typography fontWeight={600}>{s.label}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {s.description}
                        </Typography>
                      </Box>
                    )}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Paper>
        </Grid>

        {/* CONTENT */}
        <Grid item xs={12} md={9}>
          <Paper sx={{ p: 4 }}>
            {/* Client */}
            <Box sx={{ mb: 3, p: 2, borderRadius: 2, background: "var(--fx-surface-sunken, #F8FAFC)" }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <Avatar>
                    <Person />
                  </Avatar>
                </Grid>
                <Grid item xs>
                  <Typography fontWeight={600}>{clientName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    <LocationOn sx={{ fontSize: 16 }} /> {location?.address}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Timer */}
            {started && (
              <Box textAlign="center" mb={3}>
                <Timer sx={{ color: "#1976d2" }} />
                {loadingTime ? (
                  <Skeleton width={100} height={24} sx={{ mx: "auto" }} />
                ) : (
                  <Typography variant="h5" sx={{ fontFamily: "monospace" }}>
                    {formatElapsed(elapsed)}
                  </Typography>
                )}
              </Box>
            )}

            {/* Completed */}
            {finished && (
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                <Typography fontWeight={600}>Job Completed</Typography>
                <Divider sx={{ my: 1 }} />
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <AccessTime fontSize="small" /> {startTime?.format("h:mm A")}
                  </Grid>
                  <Grid item xs={4}>
                    <CheckCircle fontSize="small" /> {endTime?.format("h:mm A")}
                  </Grid>
                  <Grid item xs={4}>
                    <Timer fontSize="small" /> {formatElapsed(endTime?.diff(startTime, "second"))}
                  </Grid>
                </Grid>
              </Alert>
            )}

            {/* BUTTON */}
            <Box textAlign="center" mt={3}>
              {getButton()}
            </Box>
          </Paper>

          {/* TEAM CHAT */}
          {jobEventUid && jobChatCompanyId && (
            <Paper
              ref={jobChatRef}
              tabIndex={-1}
              sx={{
                mt: 4,
                scrollMarginTop: 16,
                overflow: "hidden",
                borderRadius: 2.5,
                border: "1px solid var(--fx-surface-border, #E2E8F0)",
                backgroundColor: "var(--fx-surface-base, #FFFFFF)",
                boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
              }}
            >
              <Box sx={{ px: 3, py: 2, borderBottom: "1px solid var(--fx-surface-border, #E2E8F0)", display: "flex", alignItems: "center", gap: 1 }}>
                <WorkOutline sx={{ color: colors.orange.base }} />
                <Typography fontWeight={600}>Job Chat</Typography>
              </Box>
              <Box sx={{ height: 480 }}>
                <JobChatView jobEventUid={jobEventUid} companyId={jobChatCompanyId} status={event?.status} jobAlias={data?.alias} />
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
